/**
 * משתמשי המשרד — user administration. OA-0001 §3.2 / §3.7, 01 §A3, 02 §8, 04 §8.2.
 *
 * ── THE COLUMN THAT CARRIES THE ARCHITECTURE ────────────────────────────────────────────────────
 *
 * `תפקידים` is legitimately EMPTY for an invited user, and the screen writes that emptiness out in
 * words rather than leaving a blank cell. OA-0001 §3.7: an invitation carries NO ROLE, so an invitee
 * arrives with no capabilities at all. A grid that rendered an empty cell there would be describing
 * a missing value; this one is describing a deliberate state.
 *
 * ── DISABLING IS A STATUS, NEVER A DELETE — AND IT NOW HAS AN INVERSE ───────────────────────────
 *
 * 02 §8: no physical delete. A disabled membership keeps its grants resolvable and its audit history
 * attributable, which is the whole reason an audit trail can still answer "who did this" about
 * somebody who left last year. So the action is `השבתה`, the row stays, and 01 §A3's seat rule is
 * visible beside it: only `invited` and `active` consume a seat.
 *
 * What was missing was the way BACK. `disable` had no inverse, so it was a one-way door and every
 * mistake needed somebody with database access. `הפעלה מחדש` is now beside it — and it restores a
 * STATUS and nothing else: the route deliberately carries no `roleCodes`, because a user who comes
 * back comes back with what they had, and changing that is a separate call under a separate
 * capability against a separate table. It restores no sessions either, and the screen says so rather
 * than leaving somebody to assume their colleague is signed in again.
 *
 * ── AN INVITATION THAT LANDED NOWHERE IS RECOVERABLE ───────────────────────────────────────────
 *
 * `שליחה חוזרת` mints a NEW token and the previous one stops resolving. Both halves are stated,
 * because an office that sent two links needs to know that only the last one works. And when the mail
 * port suppressed or refused the message the answer is a 202 carrying `delivery` — the office is told
 * the invitation EXISTS and the message did not go, which is the difference between waiting and
 * telephoning.
 *
 * ── AND READING A COLLEAGUE'S SESSIONS IS SURVEILLANCE, WHICH IS WHY IT IS AUDITED ─────────────
 *
 * `tenant.user.session.read` is `audit: 'ALWAYS'` even though it writes nothing: 04 §8.2's obligation
 * covers what was LOOKED AT as much as what was changed. The screen says so at the point of use, and
 * the payload carries no address, no device and no location — nothing that would reward the look.
 *
 * ── THE MODALS ARE MODALS ──────────────────────────────────────────────────────────────────────
 *
 * 09 §2.1: one decision, one field, no table, no drill-down, and each states its blast radius by
 * name. `Modal.tsx` enforces those mechanically; these are the cases they were written for.
 *
 * ── AND THE ROWS NOW COME FROM THE SERVER. **Phase 2 §6, vertical A.** ─────────────────────────
 *
 * This screen used to render `fixturesEnabled() ? fixtureMemberships : []`, which meant it showed
 * development data to a developer and an EMPTY OFFICE to everybody else — the same blank table
 * whether the office had no users, whether the gate refused, or whether nothing was reachable.
 *
 * `tenant.member.read` now supplies the rows through `<ServerRead>`, which keeps those apart. The
 * two secondary reads are deliberately separate calls rather than fields bolted onto the first:
 *
 *   · `tenant.role.read` supplies the Hebrew NAMES for role codes. It DEGRADES rather than blocks —
 *     if it fails the codes render raw, because nobody can administer an office while a lookup table
 *     is unavailable, and the codes are still true.
 *   · `tenant.user.session.read` is fetched by `<MemberSessions>`, which mounts only when somebody
 *     opens the panel. That is not a performance choice: the route is `audit: 'ALWAYS'`, and reading
 *     a colleague's sessions on every page load would write a surveillance record about an
 *     administrator who never looked.
 *
 * Every mutation ends in `members.reload()`. A screen that patched its local array would drift from
 * the server the moment two administrators worked at once.
 */

import { useCallback, useMemo, useState } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { ServerRead } from '../components/ServerRead';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { Column } from '../components/DataTable';
import type { MailDeliveryReason, MembershipSummary } from '../api/contract';
import { MAIL_DELIVERY_REASONS } from '../api/contract';
import { membershipStatusHe } from '../api/adminVocabulary';
import { deliveryTextFor } from '../api/deliveryText';
import {
  adminRevocationChoices,
  revocationReasonRefusalText,
  sessionRowText,
  sessionSummaryHe,
} from '../api/sessionText';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { listOf, useServerData } from '../api/useServerData';
import { routes } from '../routes';

const TITLE_HE = 'משתמשי המשרד';

/** Which modal is open. One at a time: each is one decision (09 §2.1 M3). */
type Pending =
  | { readonly kind: 'disable'; readonly member: MembershipSummary }
  | { readonly kind: 'reactivate'; readonly member: MembershipSummary }
  | { readonly kind: 'resend'; readonly member: MembershipSummary }
  | { readonly kind: 'revokeSessions'; readonly member: MembershipSummary };

type Outcome =
  | { readonly kind: 'disabled'; readonly name: string }
  | { readonly kind: 'reactivated'; readonly name: string }
  | { readonly kind: 'resent'; readonly delivery: MailDeliveryReason | null }
  | { readonly kind: 'sessionsRevoked'; readonly count: number }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

function isDeliveryReason(value: unknown): value is MailDeliveryReason {
  return typeof value === 'string' && (MAIL_DELIVERY_REASONS as readonly string[]).includes(value);
}

export function TenantUsersScreen(): JSX.Element {
  const ctx = useWorkContext();
  const [pending, setPending] = useState<Pending | null>(null);
  const [reason, setReason] = useState('');
  const [revocationReason, setRevocationReason] = useState(
    adminRevocationChoices()[0]?.reason ?? 'administrator_revoked',
  );
  const [viewingSessionsOf, setViewingSessionsOf] = useState<MembershipSummary | null>(null);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const readMembers = useCallback(() => callOperation('tenant.member.read', {}), []);
  const readRoles = useCallback(() => callOperation('tenant.role.read', {}), []);
  const members = useServerData(readMembers);
  const roles = useServerData(readRoles);

  // The names are a LOOKUP, so a failure here degrades to the codes rather than blocking the screen.
  // `listOf` yields nothing while loading or after a failure, and `roleNames.get(code) ?? code` in
  // the column below is what makes that survivable.
  const roleNames = useMemo(
    () => new Map(listOf(roles.result, 'roles').map((r) => [r.roleCode, r.displayNameHe])),
    [roles.result],
  );

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="TENANT" actual={ctx.surface} />;
  }

  function close(): void {
    setPending(null);
    setReason('');
  }

  async function run(action: () => Promise<Outcome>): Promise<void> {
    setSending(true);
    setOutcome(null);
    try {
      setOutcome(await action());
      // RE-READ, never patch. Two administrators working at once is the ordinary case in an office,
      // and a locally-patched array would show one of them a table that no longer exists.
      members.reload();
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
      close();
    }
  }

  function confirm(target: Pending): void {
    void run(async () => {
      if (target.kind === 'disable') {
        await callOperation('tenant.user.disable', {
          body: { membershipUid: target.member.membershipUid, reason },
        });
        return { kind: 'disabled', name: target.member.displayName };
      }
      if (target.kind === 'reactivate') {
        // NO `roleCodes`. Reactivation restores a STATUS; what the user may do is a separate call.
        await callOperation('tenant.user.reactivate', {
          body: { membershipUid: target.member.membershipUid, reason },
        });
        return { kind: 'reactivated', name: target.member.displayName };
      }
      if (target.kind === 'resend') {
        // 202 with `delivery` when the message did not go out. The invitation still exists.
        const result = await callOperation('tenant.user.invitation.resend', {
          params: { invitation_uid: target.member.membershipUid },
          body: {},
        });
        return {
          kind: 'resent',
          delivery: isDeliveryReason(result?.delivery) ? result.delivery : null,
        };
      }
      const result = await callOperation('tenant.user.session.revoke', {
        params: { membership_uid: target.member.membershipUid },
        // `sessionUid` ABSENT means every session of that member — the shape of the request, never a
        // boolean called `all` that defaults.
        body: { reason: revocationReason },
      });
      return { kind: 'sessionsRevoked', count: result?.revoked ?? 0 };
    });
  }

  const columns: readonly Column<MembershipSummary>[] = [
    {
      key: 'name',
      headerHe: 'שם',
      render: (m) => (
        <span className="company-cell">
          <span className="company-cell__name">{m.displayName}</span>
          <span className="company-cell__id" dir="ltr">
            {m.email}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      headerHe: 'מצב',
      render: (m) => {
        const term = membershipStatusHe(m.status);
        return <StatusChip labelHe={term.labelHe} tone={term.tone} />;
      },
    },
    {
      key: 'roles',
      headerHe: 'תפקידים',
      render: (m) =>
        m.roleCodes.length === 0 ? (
          // OA-0001 §3.7 written out. Not a blank cell: a blank cell reads as missing data.
          <span className="cell-note">ללא תפקיד — המשתמש הוזמן ללא הרשאות</span>
        ) : (
          <span className="chip-row">
            {m.roleCodes.map((code) => (
              <span key={code} className="chip">
                {roleNames.get(code) ?? code}
              </span>
            ))}
          </span>
        ),
    },
    {
      key: 'seat',
      headerHe: 'תופס מושב',
      render: (m) => (m.consumesSeat ? 'כן' : 'לא'),
    },
    {
      key: 'actions',
      headerHe: 'פעולות',
      // Every action a membership has, and each one appears where its state makes it meaningful. A
      // hidden control is presentation and never a permission decision — the gate refuses regardless.
      render: (m) => (
        <span className="chip-row">
          {m.status === 'invited' ? (
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => setPending({ kind: 'resend', member: m })}
            >
              שליחה חוזרת של ההזמנה
            </button>
          ) : null}
          {m.status === 'disabled' ? (
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => setPending({ kind: 'reactivate', member: m })}
            >
              הפעלה מחדש
            </button>
          ) : (
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => setPending({ kind: 'disable', member: m })}
            >
              השבתה
            </button>
          )}
          <button type="button" className="btn btn--quiet" onClick={() => setViewingSessionsOf(m)}>
            התחברויות
          </button>
        </span>
      ),
    },
  ];

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'ניהול המשרד', href: routes.tenantAdmin }}
      provenanceHe="השבתת משתמש היא שינוי מצב ולא מחיקה. ההרשאות וההיסטוריה שלו נשארים ניתנים לאיתור, והפעלה מחדש מחזירה את המצב בלבד."
    >
      <div className="actions-row">
        <a className="btn btn--primary" href={routes.tenantUserInvitation}>
          הזמנת משתמש
        </a>
        <a className="btn" href={routes.tenantRoles}>
          תפקידים והרשאות
        </a>
        <a className="btn" href={routes.tenantCompanyAccess}>
          הרשאות לחברות
        </a>
      </div>

      <ServerRead result={members.result} whatHe="את משתמשי המשרד">
        {(data) => (
          <DataTable<MembershipSummary>
            captionHe="משתמשים"
            columns={columns}
            rows={data.members}
            rowKey={(m) => m.membershipUid}
            // 09 §5.1: any "N" figure states which N it is. Both counts are computed from the rows
            // that ARRIVED — a count taken from anywhere else would survive a failed read and put a
            // number on the screen that describes nothing.
            filterSummaryHe={`מציג ${data.members.length} משתמשים · ${
              data.members.filter((m) => m.consumesSeat).length
            } מהם תופסים מושב במנוי`}
            emptyStateHe="אין עדיין משתמשים במשרד."
          />
        )}
      </ServerRead>

      {outcome === null ? null : outcome.kind === 'disabled' ? (
        <StateBlock
          titleHe="המשתמש הושבת"
          bodyHe={`${outcome.name} לא יוכל להתחבר. ההרשאות וההיסטוריה שלו נשמרו, והמושב שלו התפנה.`}
          nextStep={<p>אפשר להפעיל את המשתמש מחדש מאותה שורה. ההפעלה מחזירה את המצב בלבד.</p>}
        />
      ) : outcome.kind === 'reactivated' ? (
        <StateBlock
          titleHe="המשתמש הופעל מחדש"
          bodyHe={`${outcome.name} חזר למצב פעיל עם התפקידים שהיו לו קודם. ההפעלה תופסת מושב במנוי.`}
          // Said explicitly, because the opposite assumption is the dangerous one.
          nextStep={<p>ההתחברויות שנותקו בעת ההשבתה אינן חוזרות. תידרש התחברות מחדש.</p>}
        />
      ) : outcome.kind === 'resent' ? (
        (() => {
          const text = deliveryTextFor(outcome.delivery);
          return (
            <StateBlock
              titleHe={text.titleHe}
              bodyHe={text.explanationHe}
              tone={text.tone === 'ok' ? 'empty' : 'error'}
              nextStep={<p>{text.nextStepHe}</p>}
            />
          );
        })()
      ) : outcome.kind === 'sessionsRevoked' ? (
        <StateBlock
          titleHe="ההתחברויות נותקו"
          // The COUNT, never "ok". An administrator evicting a compromised account needs to know it
          // was four, and a row per session stays in the log with the reason that was recorded.
          bodyHe={`נותקו ${outcome.count} התחברויות של המשתמש. הרשומות נשמרות ביומן יחד עם הסיבה שנרשמה.`}
        />
      ) : (
        <ActionFailureNotice
          failure={outcome.failure}
          renderConflict={(failure) => {
            // The one conflict this screen can read: a revocation reason an administrator may not
            // assert. The permitted set travels as `excess`, exactly as a ceiling refusal does.
            if (failure.body.excess === undefined) return null;
            const text = revocationReasonRefusalText(failure.body.excess);
            return (
              <section className="denial" data-tone="blocked" role="alert">
                <h2 className="denial__title">{text.titleHe}</h2>
                <p className="denial__explanation">{text.explanationHe}</p>
                <p className="denial__next-step">{text.nextStepHe}</p>
              </section>
            );
          }}
        />
      )}

      {/* ── A COLLEAGUE'S SESSIONS. A read that is audited, and says so where it is performed. ── */}
      {viewingSessionsOf === null ? null : (
        <section className="notice" data-tone="info">
          <h2 className="notice__title">{`ההתחברויות של ${viewingSessionsOf.displayName}`}</h2>
          <p className="notice__body">
            צפייה בהתחברויות של עובד אחר נרשמת ביומן הביקורת יחד עם מי שצפה בה. הרשימה מציגה מתי נפתחה
            כל התחברות ומתי נראתה לאחרונה, ואינה מציגה מיקום, כתובת רשת או פרטי מכשיר.
          </p>
          <MemberSessions membershipUid={viewingSessionsOf.membershipUid} />

          <fieldset className="field field--choice">
            <legend className="field__label">סיבת הניתוק</legend>
            {/*
              Two choices, not seven. The other reasons are facts the system records about itself, and
              an administrator who could assert one of them would be writing an event that never
              happened into the audit trail.
            */}
            {adminRevocationChoices().map((choice) => (
              <label key={choice.reason} className="choice">
                <input
                  type="radio"
                  name="revocationReason"
                  value={choice.reason}
                  checked={revocationReason === choice.reason}
                  onChange={() => setRevocationReason(choice.reason)}
                />
                <span>
                  {choice.labelHe}
                  <span className="choice__hint">{choice.hintHe}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <div className="actions-row">
            <button
              type="button"
              className="btn"
              disabled={sending}
              onClick={() => setPending({ kind: 'revokeSessions', member: viewingSessionsOf })}
            >
              ניתוק כל ההתחברויות של המשתמש
            </button>
            <button type="button" className="btn" onClick={() => setViewingSessionsOf(null)}>
              סגירה
            </button>
          </div>
        </section>
      )}

      {pending === null ? null : pending.kind === 'disable' ? (
        <Modal
          intentHe="השבתת משתמש"
          blastRadiusHe={`ההשבתה תחול על ${pending.member.displayName}. המשתמש לא יוכל להתחבר, ההרשאות וההיסטוריה שלו יישמרו, והמושב שלו יתפנה.`}
          fields={1}
          confirmLabelHe={sending ? 'שולח…' : 'השבתה'}
          onConfirm={() => confirm(pending)}
          onClose={close}
        >
          <label className="field">
            <span className="field__label">סיבת ההשבתה</span>
            <input
              className="field__input"
              name="disable-reason"
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
            <span className="field__hint">הסיבה נרשמת ביומן הביקורת יחד עם מי שביצע את הפעולה.</span>
          </label>
        </Modal>
      ) : pending.kind === 'reactivate' ? (
        <Modal
          intentHe="הפעלת משתמש מחדש"
          blastRadiusHe={`${pending.member.displayName} יחזור למצב פעיל עם התפקידים שהיו לו לפני ההשבתה, ויתפוס מושב במנוי. ההתחברויות שנותקו אינן חוזרות.`}
          fields={1}
          confirmLabelHe={sending ? 'שולח…' : 'הפעלה מחדש'}
          onConfirm={() => confirm(pending)}
          onClose={close}
        >
          <label className="field">
            <span className="field__label">סיבת ההפעלה מחדש</span>
            <input
              className="field__input"
              name="reactivate-reason"
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
            <span className="field__hint">
              ההפעלה מחזירה מצב בלבד. שינוי התפקידים נעשה במסך תפקידים והרשאות.
            </span>
          </label>
        </Modal>
      ) : pending.kind === 'resend' ? (
        <Modal
          intentHe="שליחה חוזרת של ההזמנה"
          blastRadiusHe={`תישלח הזמנה חדשה אל ${pending.member.displayName}. הקישור הקודם יפסיק לפעול באותו רגע, ולכן רק הקישור החדש יעבוד.`}
          fields={0}
          confirmLabelHe={sending ? 'שולח…' : 'שליחה חוזרת'}
          onConfirm={() => confirm(pending)}
          onClose={close}
        >
          <p className="field__hint">
            ההזמנה אינה מקנה הרשאות. המוזמן מגיע ללא תפקיד וללא גישה לחברות, בדיוק כמו בהזמנה
            הראשונה.
          </p>
        </Modal>
      ) : (
        <Modal
          intentHe="ניתוק ההתחברויות של המשתמש"
          blastRadiusHe={`כל ההתחברויות של ${pending.member.displayName} יסתיימו והוא יידרש להתחבר מחדש. ההרשאות שלו אינן משתנות, ודבר אינו נמחק.`}
          fields={0}
          confirmLabelHe={sending ? 'שולח…' : 'ניתוק ההתחברויות'}
          onConfirm={() => confirm(pending)}
          onClose={close}
        >
          <p className="field__hint">
            הסיבה שנבחרה נרשמת ביומן על כל התחברות שנותקה. סיבות אחרות הן עובדות שהמערכת רושמת בעצמה
            ואינן ניתנות לקביעה כאן.
          </p>
        </Modal>
      )}
    </AppShell>
  );
}

/**
 * A COLLEAGUE'S SESSIONS, READ ONLY WHEN SOMEBODY ASKS.
 *
 * This is a component rather than a third `useServerData` in the screen, and the reason is the audit
 * obligation: `tenant.user.session.read` is `audit: 'ALWAYS'` (04 §8.2), so a hook that fired on
 * every page load would write a row saying an administrator looked at a colleague's devices when
 * nobody had. Mounting IS the request, and the panel mounts on the click that means it.
 *
 * The failure states matter more here than on most reads. "No sessions" and "we could not tell you"
 * are opposite conclusions about whether a departing employee is still signed in somewhere, and an
 * administrator acting on the wrong one leaves a live session on a laptop that walked out.
 */
function MemberSessions({ membershipUid }: { readonly membershipUid: string }): JSX.Element {
  const read = useCallback(
    () => callOperation('tenant.user.session.read', { params: { membership_uid: membershipUid } }),
    [membershipUid],
  );
  const sessions = useServerData(read);

  return (
    <ServerRead result={sessions.result} whatHe="את ההתחברויות" skeletonRows={3}>
      {(data) => (
        <>
          <p className="notice__body">{sessionSummaryHe(data.sessions)}</p>
          {data.sessions.map((session) => {
            const row = sessionRowText(session);
            return (
              <p key={session.sessionUid} className="notice__body">
                {`${row.stateHe} · ${row.startedHe}`}
                {row.lastSeenHe === null ? '' : ` · ${row.lastSeenHe}`}
              </p>
            );
          })}
        </>
      )}
    </ServerRead>
  );
}
